import { TAppDispatch, TGetState } from 'store/types'
import {
  getAiImgHistory_API,
  deleteAiImageProject_API,
  deleteAiImageProjectByProjectId_API,
  getAiImgModels_API,
  getAiImgStyles_API
} from 'api'
import {
  IAIImageModel,
  IGeneratedAIImage,
  IGetHistoryParams,
  IReferenceImage
} from 'store/types/aiImage'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { ErrorKeys } from 'consts/errorKeys'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { getClosestAspectRatio } from 'store/features/aiImage/utils'
import { getInitialGenerateFormState } from 'store/features/aiImage/initialState'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { captureException } from '@sentry/react'
import { projectSelectors } from 'store/features/project/selectors'
import aiImageSlice from 'store/features/aiImage/slice'
import { handleApiError } from 'store/utils'
import { uploadThunks } from './upload'
import { generateThunks } from './generate'
import { formThunks } from './form'
import { versionHistoryThunks } from './versionHistory'
const { actions } = aiImageSlice

/**
 * Thunk to fetch AI image models
 */
const getModels = () => async (dispatch: TAppDispatch, getState: TGetState) => {
  const state = getState()
  const modelsData = aiImageSelectors.modelsData(state)
  if (modelsData.length > 0) {
    return modelsData
  }
  dispatch(actions.setModelsIsLoading(true))
  try {
    const response = await getAiImgModels_API()
    const models = response.data
    const defaultModel = models.find(model =>
      model.isDefaultFor.includes('v-editor')
    )
    const aiFlowImageModelCode = projectSelectors.aiFlowImageModelCode(state)
    dispatch(actions.setModels(models))
    dispatch(actions.setModelCode(aiFlowImageModelCode ?? defaultModel!.code))
    return models
  } catch (error) {
    if (!(error instanceof ApiError)) {
      captureException(new ReduxThunkError(error))
    }
    dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
  } finally {
    dispatch(actions.setModelsIsLoading(false))
  }
  return []
}

const withModels =
  <T>(
    thunk: (
      dispatch: TAppDispatch,
      getState: TGetState,
      modelsData: IAIImageModel[]
    ) => T
  ) =>
  async (dispatch: TAppDispatch, getState: TGetState): Promise<T> => {
    const modelsData = await dispatch(getModels())
    return thunk(dispatch, getState, modelsData)
  }
/**
 * Thunk to fetch AI image styles
 */
const getStyles = () => async (dispatch: TAppDispatch, getState: TGetState) => {
  const state = getState()
  dispatch(actions.setStylesIsLoading(true))
  try {
    const response = await getAiImgStyles_API()
    dispatch(actions.setStyles(response.data))
    const defaultStyle = projectSelectors.metaData(state)?.styleId
    if (defaultStyle) {
      dispatch(actions.setStyleId(defaultStyle))
    }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      captureException(new ReduxThunkError(error))
    }
    dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
  } finally {
    dispatch(actions.setStylesIsLoading(false))
  }
}

/**
 * Thunk to fetch AI image restyling styles
 */
const getRestylingStyles = () => async (dispatch: TAppDispatch) => {
  dispatch(actions.setRestylingStylesIsLoading(true))
  try {
    const response = await getAiImgStyles_API('v-editor-restyle')
    dispatch(actions.setRestylingStyles(response.data))
  } catch (error) {
    if (!(error instanceof ApiError)) {
      captureException(new ReduxThunkError(error))
    }
    dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
  } finally {
    dispatch(actions.setRestylingStylesIsLoading(false))
  }
}

/**
 * Thunk to fetch AI image history (for the sidebar)
 * @param params - The parameters for the history fetch
 */

const getHistory =
  (params: IGetHistoryParams) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const currentHistory = aiImageSelectors.historyData(state) || []
    const isScoped = aiImageSelectors.isHistoryScopedToCurrentProject(state)
    const currentProjectId = projectSelectors.id(state)

    const isEmptyHistory = isScoped && !currentProjectId
    if (isEmptyHistory) return

    dispatch(actions.setHistoryIsLoading(true))
    try {
      const response = await getAiImgHistory_API({
        ...params,
        videoProjectId: isScoped ? Number(currentProjectId) : undefined
      })
      const { imageProjects, totalCount } = response.data

      if (!params.offset) {
        dispatch(actions.setHistoryData(imageProjects))
      } else {
        dispatch(actions.setHistoryData([...currentHistory, ...imageProjects]))
      }

      dispatch(actions.setHistoryCount(totalCount))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setHistoryIsLoading(false))
    }
  }

/**
 * Future feature
 * Thunk to delete an AI image project
 * @param imageId - The ID of the AI image (not the project id)
 */

const deleteImageProject =
  (imageId: number) => async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const historyDataLength = aiImageSelectors.historyDataLength(state)

    dispatch(actions.setDeleteItemId(imageId))

    try {
      const response = await deleteAiImageProject_API(imageId)
      const { succeeded, failed } = response.data as {
        succeeded: number[]
        failed: number[]
      }

      if (succeeded.length) {
        const historyLength = historyDataLength || 0
        dispatch(getHistory({ offset: 0, limit: historyLength }))
      }

      if (failed.length) {
        //TODO: add popup to dont allow delete
      }
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.removeDeleteItemId(imageId))
    }
  }

/**
 * Thunk to add reference images to the AI image generation form
 * @param imageData - The reference images to add
 */

const addReferenceImage =
  (imageData: IReferenceImage[]) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const availableRefImgsCount = aiImageSelectors.availableRefImgsCount(state)
    const refImages = aiImageSelectors.currentModelRefImages(state)

    if (availableRefImgsCount > 0) {
      const slicedRefImages = imageData.slice(0, availableRefImgsCount)
      const newRefImages = [...(refImages || []), ...slicedRefImages]

      dispatch(actions.setReferenceImages(newRefImages))
    }
  }

/**
 * Thunk to replace reference images in the AI image generation form
 * @param imageData - The reference images to replace the existing ones
 */

const replaceReferenceImages =
  (imageData: IReferenceImage[]) => async (dispatch: TAppDispatch) => {
    dispatch(actions.setReferenceImages(imageData))
  }

const setModelCode = (modelCode?: string) =>
  withModels(
    async (
      dispatch: TAppDispatch,
      getState: TGetState,
      modelsData: IAIImageModel[]
    ) => {
      const targetModelCode =
        modelCode ??
        projectSelectors.aiFlowImageModelCode(getState()) ??
        getInitialGenerateFormState().modelCode
      const state = getState()
      const { aspectRatioString: projectAspectRatio } =
        projectDataSelectors.defaultDimensions(state)
      const currentAspectRatio = aiImageSelectors.aspectRatio(state)
      const newModel =
        modelsData.find(model => model.code === targetModelCode) ||
        modelsData.find(model => model.isDefaultFor.includes('v-editor'))!

      // Get target aspect ratio - use project ratio if current matches default, otherwise keep current
      const defaultAspectRatio = getClosestAspectRatio(
        projectAspectRatio,
        aiImageSelectors.aspectRatios(state)
      )
      const targetAspectRatio =
        !currentAspectRatio || currentAspectRatio === defaultAspectRatio
          ? projectAspectRatio
          : currentAspectRatio

      // Set new model and closest matching aspect ratio
      dispatch(actions.setModelCode(newModel.code))
      dispatch(
        actions.setAspectRatio(
          getClosestAspectRatio(
            targetAspectRatio,
            newModel.possibleAspectRatios
          )
        )
      )
    }
  )

const resetGenerateForm = () =>
  withModels((dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const projectMeta = projectSelectors.metaData(state)
    const defaultModel = aiImageSelectors.defaultModel(state)
    const aiFlowImageModelCode = projectSelectors.aiFlowImageModelCode(state)
    dispatch(actions.resetGenerateForm())
    dispatch(actions.setModelCode(aiFlowImageModelCode ?? defaultModel.code))
    if (projectMeta) {
      dispatch(actions.setStyleId(projectMeta.styleId))
    }
  })

const updateHistoryItem =
  (imageId: number, updates: IGeneratedAIImage) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const historyData = aiImageSelectors.historyData(getState())
    if (!historyData) return

    const updatedHistory = historyData.map(project => {
      if (!project.images.some(image => image.id === imageId)) return project
      return {
        ...project,
        images: project.images.map(image =>
          image.id === imageId ? { ...image, ...updates } : image
        )
      }
    })

    dispatch(actions.setHistoryData(updatedHistory))
  }

const deleteHistoryItem =
  (imageId: number) => (dispatch: TAppDispatch, getState: TGetState) => {
    const historyData = aiImageSelectors.historyData(getState())
    if (!historyData) return

    const updatedHistory = historyData.filter(
      project => !project.images.some(image => image.id === imageId)
    )

    dispatch(actions.setHistoryData(updatedHistory))
  }

/**
 * Thunk to delete an AI image project by project ID
 * @param projectId - The ID of the AI image project
 */
const deleteProjectById =
  (projectId: number) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const historyData = aiImageSelectors.historyData(state)
    if (!historyData) return

    // Find the image ID for this project (for loading state)
    const project = historyData.find(p => p.id === projectId)
    if (!project) return

    const imageId = project.images[0]?.id
    if (!imageId) return

    dispatch(actions.setDeleteItemId(imageId))

    try {
      await deleteAiImageProjectByProjectId_API(projectId)

      // Remove the project from history
      const updatedHistory = historyData.filter(p => p.id !== projectId)
      dispatch(actions.setHistoryData(updatedHistory))
      dispatch(actions.setHistoryCount(updatedHistory.length))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.removeDeleteItemId(imageId))
    }
  }

export const aiImageThunks = {
  ...actions,
  ...uploadThunks,
  ...generateThunks,
  ...formThunks,
  ...versionHistoryThunks,
  getStyles,
  getRestylingStyles,
  getModels,
  getHistory,
  addReferenceImage,
  replaceReferenceImages,
  deleteImageProject,
  deleteProjectById,
  setModelCode,
  resetGenerateForm,
  updateHistoryItem,
  deleteHistoryItem
}
